import LivePreviewBase from "./live_preview_base"
import {
  applyTextStrokeToElements,
  applyFontSizeScale,
  applyTypographyToElements,
  applyTextShadowToElements,
} from "../utils/text_treatment"
import { TYPE_PAIR_SPECS } from "../services/language_learning_type_pairs"

// Live-preview controller for the Cloze Passage wizard.
//
// This controller has TWO jobs, and only the first is the usual one.
//
// JOB 1 — repaint the frame-0 mirror. Frame 0 on this template is the PAYOFF
// pole with a task attached: the whole passage and the whole word bank are
// painted at opacity 1, and the ONLY thing the video defers is the PLACEMENT of
// words that are already on screen. Every applier here therefore repaints
// something already visible. Three consequences, each a law rather than a style
// choice:
//
//   1. Nothing here touches `opacity`, ever. The reveal is a hard content swap
//      inside a fixed box plus a translate/scale settle at constant opacity;
//      there is no fade in this template and no fade-out at the end. There is
//      no opacity axis to animate.
//   2. Nothing here fills a blank. The gaps are empty at frame 0 and each line
//      fills about two seconds apart — that is the retrieval act, not a
//      withhold, because every answer is already painted in the bank. A preview
//      that pre-filled a slot would be previewing the finished screenshot
//      instead of the exercise.
//   3. The PASSAGE is deliberately NOT live-bound as ink. Two derived
//      quantities depend on all the lines at once — the fitted font size (the
//      solver is WIDTH-bound: `880 / max(lineEm)`) and every blank's pre-sized
//      slot width (`blankEm(answer)`, which depends on the ANSWER ROWS, not the
//      passage) — and both are computed server-side from the same numbers the
//      renderer uses. A JS copy would be a third derivation, which is precisely
//      how a preview starts lying about the render, so passage edits repaint on
//      save (the T1/T2/T3/T4/T8 posture). Everything that does NOT feed those
//      two fits — hook, gloss, CTA, the two languages, the level badge, the
//      bank toggle, theme, typography, size, stroke, shadow — updates live.
//
// JOB 2 — keep the ANSWER ROWS bound to the passage. This is unique to T5 and
// it is why the template ships with no row CRUD at all: blank *i* IS the *i*-th
// `___` run in the text, so "add a blank" is not a button, it is typing `___`.
// On every keystroke this controller re-parses the passage with the same
// `/_{2,}/` law the builder uses, adds or removes answer rows to match the run
// count, updates the line/blank/beat counters, and surfaces the lone-`_` and
// count-mismatch warnings inline — before a save, rather than as a raise after
// one. It never renumbers by index in the input NAMES (they stay `blanks[][…]`,
// the `[]` collection shape), because position in the document IS the binding.
//
// TYPE_PAIR_SPECS is imported from the shared language-learning catalog
// (de-mirrored — the font data is authored once in the TS source and consumed by
// both legs), so the preview can never drift from what the renderer paints.
//
// Mounted alongside `palette-live-preview`, which owns the background layers +
// tint/darkness sliders. Inherits event wiring + dispatch + value normalisation
// from LivePreviewBase.
export default class extends LivePreviewBase {
  static targets = [
    "frame", "flag", "languageLabel", "clozeBadge", "levelBadge", "countPill",
    "hookText", "passageGloss", "passage",
    "bank", "bankLabel", "chipLabel", "chipGloss",
    "ctaText",
    // Job 2 — the content step's own DOM (the form side, not the preview).
    "passageInput", "answerRows", "answerRow",
    "lineCounter", "blankCounter", "beatCounter",
    "loneUnderscoreWarning", "countMismatchWarning", "mismatchRuns", "mismatchAnswers",
  ]

  static FIELD_BINDINGS = {
    hook_text:            { apply: "applyHookText" },
    passage_gloss:        { apply: "applyPassageGloss" },
    cta_text:             { apply: "applyCtaText" },
    passage_text:         { apply: "applyPassageSync" },
    difficulty:           { apply: "applyDifficulty" },
    show_answer_bank:     { apply: "applyBankVisibility" },
    target_language_code: { apply: "applyTargetLanguage" },
    learner_locale:       { apply: "applyLearnerLocale" },
    theme:                { apply: "applyTheme" },
    type_pair:            { apply: "applyTypography" },
    italic_override:      { apply: "applyTypography" },
    font_size_scale:      { apply: "applyFontSize" },
    display_override:     { apply: "applyFontSize" },
    body_override:        { apply: "applyFontSize" },
    text_shadow:          { apply: "applyTextShadow" },
    text_stroke_color:    { apply: "applyStroke" },
    text_stroke_width:    { apply: "applyStroke" },
  }

  // -- chrome ---------------------------------------------------------------

  applyHookText() {
    this.setText("hookText", stripLanguageTags(this.fieldValue("hook_text")) || "Your hook goes here.")
  }

  applyPassageGloss({ value }) { this.setText("passageGloss", value) }

  applyCtaText() { this.setText("ctaText", this.fieldValue("cta_text")) }

  // The CEFR badge is painted LITERALLY — "A1" is locale-free, so unlike every
  // other chrome string it is never translated and never localised.
  applyDifficulty({ value }) { this.setText("levelBadge", value) }

  // The bank band is the frame-0 claim: with it on, every answer is already
  // painted before the first gap is asked, so the pause is a retrieval rather
  // than a withhold. Turning it off changes what frame 0 promises, so the
  // preview hides the band instead of dimming it.
  applyBankVisibility({ value }) {
    if (!this.hasBankTarget) return
    this.bankTarget.hidden = value === false
  }

  // The VOICED language of the passage: the hero flag and the language label.
  // Base subtag throughout, so "en-GB" flags as 🇬🇧 — matching
  // ClozePassage::ConfigBuilder#resolved_target_flag_emoji, which splits the
  // subtag before the lookup.
  applyTargetLanguage({ value }) {
    this.setText("languageLabel", languageName(value, this.learnerLocale()))
    if (this.hasFlagTarget) this.flagTarget.textContent = FLAGS[baseSubtag(value)] || "🏳️"
  }

  // The READ language — the glosses, the badge and the bank label resolve in
  // it. On this template it is also the HOOK's spoken language: the one
  // cross-language take in the video.
  applyLearnerLocale({ value }) {
    const locale = value || "en"
    this.setText("clozeBadge", CLOZE_BADGE[baseSubtag(locale)] || CLOZE_BADGE.en)
    this.setText("bankLabel", BANK_LABEL[baseSubtag(locale)] || BANK_LABEL.en)
    this.setText("languageLabel", languageName(this.fieldValue("target_language_code"), locale))
  }

  // The palette drives every ink on the frame: the passage takes `--p-text`,
  // the gloss and the chip glosses take `--p-muted`, and the ACCENT carries the
  // three signals that matter — the empty blank's underline, the answer as it
  // lands, and the active line's bar. Swapping the custom properties repaints
  // all of them without touching a single opacity.
  applyTheme({ value }) {
    if (!this.hasFrameTarget) return
    const palette = THEME_PALETTES[value] || THEME_PALETTES.notebook
    const f = this.frameTarget
    f.style.setProperty("--p-bg", palette.bg)
    f.style.setProperty("--p-surface", palette.surface)
    f.style.setProperty("--p-text", palette.text)
    f.style.setProperty("--p-muted", palette.muted)
    f.style.setProperty("--p-accent", palette.accent)
  }

  // -- job 2: the passage → answer-row binding ------------------------------

  // Re-parse on every keystroke and bring the editor back into line. This is
  // the whole reason the template has no add/remove buttons.
  applyPassageSync() {
    const raw = this.passageValue()
    const lines = passageLines(raw)
    const perLine = lines.map((line) => (line.match(BLANK_RUN_RE) || []).length)
    const runs = perLine.reduce((total, n) => total + n, 0)
    const beats = perLine.filter((n) => n > 0).length

    this.setText("lineCounter", `${lines.length} ${plural(lines.length, "line")}`)
    this.setText("blankCounter", `${runs} ${plural(runs, "blank")}`)
    this.setText("beatCounter", `${beats} ${plural(beats, "beat")}`)

    this.toggleWarn("loneUnderscoreWarning", LONE_UNDERSCORE_RE.test(normalizePassage(raw)))
    this.syncAnswerRows(runs)
    this.setText("countPill", `×${runs}`)
  }

  // Add or remove rows so there is exactly one per `___`, cloning the DOM of an
  // existing row rather than building markup here — the row's classes,
  // placeholders and input NAMES are the server's business, and the names must
  // stay the `[]` collection shape (position in the document IS the binding, so
  // an indexed name would break it).
  syncAnswerRows(runs) {
    if (!this.hasAnswerRowsTarget) return
    const rows = this.answerRowTargets
    if (!rows.length) return

    if (runs > rows.length) {
      const template = rows[rows.length - 1]
      for (let i = rows.length; i < runs; i++) {
        const clone = template.cloneNode(true)
        clone.querySelectorAll("input").forEach((input) => { input.value = "" })
        this.answerRowsTarget.appendChild(clone)
      }
    } else if (runs > 0 && runs < rows.length) {
      // Trailing rows only: row i is the i-th gap, so the ones that lost their
      // gap are always at the end.
      for (let i = rows.length - 1; i >= runs; i--) rows[i].remove()
    }

    this.renumberRows()
    this.updateMismatch(runs)
  }

  renumberRows() {
    this.answerRowTargets.forEach((row, i) => {
      const num = row.querySelector(".cp-row__num")
      if (num) num.textContent = String(i + 1)
    })
  }

  // The SAVED answer count is what the render gate compares against, and it
  // only changes on save — so while the passage is being edited the warning
  // reports the stored figure, not the on-screen row count.
  updateMismatch(runs) {
    const saved = parseInt(this.mismatchAnswersTarget?.dataset?.savedCount ?? "", 10)
    const savedCount = Number.isFinite(saved)
      ? saved
      : parseInt(this.mismatchAnswersTarget?.textContent ?? "", 10)
    if (this.hasMismatchRunsTarget) this.mismatchRunsTarget.textContent = String(runs)
    this.toggleWarn("countMismatchWarning", runs > 0 && Number.isFinite(savedCount) && savedCount !== runs)
  }

  toggleWarn(targetName, show) {
    const has = `has${capitalize(targetName)}Target`
    if (!this[has]) return
    this[`${targetName}Target`].hidden = !show
  }

  passageValue() {
    if (this.hasPassageInputTarget) return this.passageInputTarget.value
    const value = this.fieldValue("passage_text")
    return typeof value === "string" ? value : ""
  }

  // -- typography / font-size / stroke --------------------------------------

  // DISPLAY role: the hook and the passage lines — the two surfaces that sit
  // over a background asset and the two the renderer strokes. BODY role: the
  // gloss, the badges, the pills, the bank chips and the CTA, which sit on the
  // theme surface and skip the stroke (a stroked answer inside a pre-sized slot
  // would visually overflow the box it must not resize).
  applyTypography() {
    const spec = TYPE_PAIR_SPECS[this.fieldValue("type_pair") || "default"]
    if (!spec) return
    applyTypographyToElements(
      { display: this.displayTargets(), body: this.bodyTargets() },
      spec,
      this.fieldValue("italic_override") || "auto",
    )
  }

  applyFontSize() {
    const globalScale = positiveOr(this.fieldValue("font_size_scale"), 1)
    const displayScale = this.readPositiveOverride("display_override") ?? globalScale
    const bodyScale    = this.readPositiveOverride("body_override")    ?? globalScale

    // The passage is sized as a UNIT — every line, and every pre-sized slot
    // inside it, scales with the block's own em — so the base size lives on the
    // container and the lines inherit it. That is what keeps the no-reflow
    // guarantee true at every font_size_scale.
    applyFontSizeScale(this.displayTargets().map((el) => sizedPair(el, 13)), displayScale)
    applyFontSizeScale(this.bodyTargets().map((el) => sizedPair(el, 9)), bodyScale)
  }

  // Stroke rides the opted-in elements only — the hook + the passage lines.
  // The badges, the pills, the gloss and the bank chips skip, mirroring the
  // renderer's applyStroke: false on those roles.
  applyStroke() {
    const targets = Array.from(
      this.element.querySelectorAll("[data-stroke-target]"),
    ).map((el) => ({
      element: el,
      basePx: parseFloat(el.dataset.strokeBasePx) || undefined,
      skip: el.dataset.strokeSkip === "true",
    }))
    if (!targets.length) return
    applyTextStrokeToElements(targets, {
      width: this.fieldValue("text_stroke_width"),
      color: this.fieldValue("text_stroke_color") || "#000000",
    })
  }

  applyTextShadow({ value }) {
    applyTextShadowToElements(
      Array.from(this.element.querySelectorAll("[data-stroke-target]")),
      value,
    )
  }

  readPositiveOverride(fieldName) {
    for (const el of this.fieldElements(fieldName)) {
      if (el.type === "range" && !el.disabled) {
        const v = parseFloat(el.value)
        return Number.isFinite(v) && v > 0 ? v : null
      }
    }
    return null
  }

  // -- preview DOM helpers --------------------------------------------------

  learnerLocale() {
    return this.fieldValue("learner_locale") || "en"
  }

  displayTargets() {
    return [
      this.hasHookTextTarget ? this.hookTextTarget : null,
      this.hasPassageTarget ? this.passageTarget : null,
    ].filter(Boolean)
  }

  // Everything on the theme surface: the badges, the pills, the gloss, the bank
  // label, every chip + chip gloss, and the CTA pill.
  bodyTargets() {
    const singles = [ "clozeBadge", "levelBadge", "countPill", "passageGloss", "bankLabel", "ctaText" ]
      .filter((name) => this[`has${capitalize(name)}Target`])
      .map((name) => this[`${name}Target`])
    return [ ...singles, ...this.chipLabelTargets, ...this.chipGlossTargets ]
  }

  setText(targetName, value) {
    const has = `has${capitalize(targetName)}Target`
    if (this[has]) this[`${targetName}Target`].textContent = value
  }
}

// THE `___` LAW, mirrored from ClozePassage::ConfigBuilder::BLANK_RUN_RE /
// LONE_UNDERSCORE_RE. A run of two or more underscores is ONE blank whatever
// its length; a LONE underscore is refused, which is what makes the banned
// spaced form `_ _ _` unauthorable (every one of its underscores is lone).
const BLANK_RUN_RE = /_{2,}/g
const LONE_UNDERSCORE_RE = /(?<!_)_(?!_)/

// ClozePassage::ConfigBuilder.normalize_passage_text: CRLF → LF, then the CSV
// two-character literal `\n` → LF. A real newline still works; the two forms
// are equivalent.
function normalizePassage(raw) {
  return (raw || "").toString().replace(/\r\n/g, "\n").replace(/\r/g, "\n").replace(/\\n/g, "\n")
}

// ClozePassage::ConfigBuilder.passage_lines: non-empty lines only. Blank lines
// in the source are formatting, never beats.
function passageLines(raw) {
  return normalizePassage(raw).split("\n").map((line) => line.trim()).filter((line) => line.length)
}

function plural(count, word) {
  return count === 1 ? word : `${word}s`
}

function capitalize(name) {
  return name[0].toUpperCase() + name.slice(1)
}

// "en-GB" → "en". The flag and name lookups are keyed on the base subtag.
function baseSubtag(code) {
  return (code || "").toString().split("-")[0].toLowerCase()
}

// The label is localized in the VIEWER's language, mirroring
// LanguageNameResolver.call(code, locale: learner_locale) on the server — a
// Modo video (viewer L1 = es) reads "Inglés", not "English".
function languageName(code, learnerLocale) {
  const base = baseSubtag(code)
  const table = LANGUAGE_NAMES[baseSubtag(learnerLocale)] || LANGUAGE_NAMES.en
  return table[base] || LANGUAGE_NAMES.en[base] || base.toUpperCase()
}

// Every scaled element carries its renderer-relative base size in
// `data-base-font-size` (server-rendered), so the JS never restates the
// preview's size table — the fallback is only for a hand-built DOM.
function sizedPair(element, fallbackBasePx) {
  return { element, basePx: parseFloat(element.dataset.baseFontSize) || fallbackBasePx }
}

function positiveOr(value, fallback) {
  if (value === null || value === undefined) return fallback
  const n = typeof value === "number" ? value : parseFloat(value)
  return Number.isFinite(n) && n >= 0 ? n : fallback
}

// Authoring language spans (<en>…</en>, <es>…</es>) are never drawn. On T5 they
// are legal ONLY in the hook — the builder RAISES on a tag inside the passage
// or an answer — so stripping is for the hook's sake and so a hand-edited
// payload still draws clean.
function stripLanguageTags(text) {
  return (text || "").replace(/<\/?[a-zA-Z][a-zA-Z-]*>/g, "").replace(/\s+/g, " ").trim()
}

// All seven language-learning looks, including the two channel-locked ones — a
// channel-bound config CAN carry `cafecito` / `modo`, and repainting it as
// notebook would be the preview lying about the render.
const THEME_PALETTES = {
  "notebook":   { "bg": "#FAF7EC", "surface": "#FFFFFF", "text": "#1F2937", "muted": "#6B7280", "accent": "#E8A838" },
  "chalkboard": { "bg": "#1F2A24", "surface": "#243530", "text": "#F4F3EC", "muted": "#C5C2AE", "accent": "#FFE066" },
  "whiteboard": { "bg": "#F8FAFC", "surface": "#FFFFFF", "text": "#111827", "muted": "#4B5563", "accent": "#DC2626" },
  "cork_board": { "bg": "#C08B5C", "surface": "#FEF3C7", "text": "#1C1917", "muted": "#57534E", "accent": "#EAB308" },
  "minimal":    { "bg": "#FFFFFF", "surface": "#F8FAFC", "text": "#0F172A", "muted": "#64748B", "accent": "#6366F1" },
  "cafecito":   { "bg": "#F6EDDF", "surface": "#FFFBF2", "text": "#3B2A1E", "muted": "#6E5A47", "accent": "#C98A2D" },
  "modo":       { "bg": "#1E2733", "surface": "#28323F", "text": "#F2F5F9", "muted": "#A8B4C2", "accent": "#FFB020" },
}

// The franchise pill and the bank label, mirroring
// `niche_templates.cloze_passage.*` in the three shipped locale files. The
// instruction-first framing is the whole reason this template's hook is voiced.
const CLOZE_BADGE = {
  "en": "COMPLETE THE TEXT",
  "es": "COMPLETA EL TEXTO",
}

const BANK_LABEL = {
  "en": "Word bank",
  "es": "Banco de palabras",
}

// Mirrors config/locales/language_names.{en,es}.yml for the languages the
// content step offers. An unknown pair falls back to the English name, then to
// the upper-cased code — the same degrade the server's `default:` gives.
const LANGUAGE_NAMES = {
  en: {
    "ar": "Arabic", "de": "German", "en": "English", "es": "Spanish", "fr": "French",
    "hi": "Hindi", "it": "Italian", "ja": "Japanese", "ko": "Korean", "nl": "Dutch",
    "pl": "Polish", "pt": "Portuguese", "ru": "Russian", "sv": "Swedish", "th": "Thai",
    "tr": "Turkish", "vi": "Vietnamese", "zh": "Chinese",
  },
  es: {
    "ar": "Árabe", "de": "Alemán", "en": "Inglés", "es": "Español", "fr": "Francés",
    "hi": "Hindi", "it": "Italiano", "ja": "Japonés", "ko": "Coreano", "nl": "Neerlandés",
    "pl": "Polaco", "pt": "Portugués", "ru": "Ruso", "sv": "Sueco", "th": "Tailandés",
    "tr": "Turco", "vi": "Vietnamita", "zh": "Chino",
  },
}

const FLAGS = {
  "ar": "🇸🇦", "de": "🇩🇪", "en": "🇬🇧", "es": "🇪🇸", "fr": "🇫🇷",
  "hi": "🇮🇳", "it": "🇮🇹", "ja": "🇯🇵", "ko": "🇰🇷", "nl": "🇳🇱",
  "pl": "🇵🇱", "pt": "🇵🇹", "ru": "🇷🇺", "sv": "🇸🇪", "th": "🇹🇭",
  "tr": "🇹🇷", "vi": "🇻🇳", "zh": "🇨🇳",
}
